import { Link } from '@tanstack/react-router'
import { useAuth } from '../../contexts/AuthContext'
import { useInStanding } from '../../hooks/useInStanding'
import { Brandlock } from './Brandlock'
import { ClubLinkButton } from './ClubButton'
import { Container } from './Container'

const navLinkClass =
  'rounded-club-sm text-[13.5px] font-medium tracking-[0.02em] text-ink transition-colors hover:text-fairway'

/**
 * The public club chrome: brandlock, section links, and the way in.
 *
 * The design card hides the nav entirely below 860px, which would leave a
 * phone with no navigation at all — so instead the bar WRAPS: brandlock on the
 * first row, links and CTA on the second. Everything stays reachable at every
 * width, with no disclosure widget to build or explain.
 *
 * The way in follows the session: a visitor gets Sign in and Apply (the one
 * `flag` the chrome spends), a member gets The Clubhouse. And the Membership
 * link — the terms and the tiers, payment material — is withheld from a member
 * in standing, as Apply already is from anyone signed in (decision record
 * §12.2); a visitor, a lapsed member and a billing-dark build see it, because
 * for them it is the next step rather than a second bill.
 *
 * `apply={false}` withholds the pill from a visitor as well. That is the
 * `invite` chrome (`ClubPage`), worn by the two tokened invitation pages,
 * whose own accept CTA is the page's one flag — the club spends one per page,
 * and every invitee is signed out. Nothing else moves: the nav, Sign in, and
 * The Clubhouse for anyone signed in are exactly as above.
 */
interface ClubHeaderProps {
  /** Offer a visitor the Apply pill. Default true; `chrome="invite"` passes false. */
  apply?: boolean
}

export function ClubHeader({ apply = true }: ClubHeaderProps) {
  const { isAuthenticated } = useAuth()
  const inStanding = useInStanding()

  return (
    <header className="sticky top-0 z-20 border-b border-brass/35 bg-paper/85 backdrop-blur-md">
      <Container className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3 py-3 sm:min-h-[74px] sm:flex-nowrap sm:py-0">
        <Brandlock />

        <nav aria-label="Club" className="flex flex-wrap items-center gap-x-6 gap-y-2 sm:gap-x-[30px]">
          <Link to="/the-club" className={navLinkClass}>
            The Club
          </Link>
          {!inStanding && (
            <Link to="/membership" className={navLinkClass}>
              Membership
            </Link>
          )}

          <span aria-hidden="true" className="hidden h-5 w-px bg-brass/40 sm:block" />

          {isAuthenticated ? (
            <ClubLinkButton to="/clubhouse" variant="ink" size="sm">
              The Clubhouse
            </ClubLinkButton>
          ) : (
            <>
              <Link
                to="/sign-in"
                className="rounded-club-sm text-[13.5px] font-semibold text-fairway"
              >
                Sign in
              </Link>
              {apply && (
                <ClubLinkButton to="/membership" size="sm">
                  Apply
                </ClubLinkButton>
              )}
            </>
          )}
        </nav>
      </Container>
    </header>
  )
}
